import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import MainLayout from './layouts/MainLayout';
import ProtectedRoute from './components/Common/ProtectedRoute';
import SmartRedirect from './components/Common/SmartRedirect';
import PWAInstallPrompt from './components/Common/PWAInstallPrompt';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { initializeAuth, selectIsAuthenticated, selectIsLoading } from './store/slices/authSlice';
import { CircularProgress, Box } from '@mui/material';
import { APP_CONFIG } from './config/app';
import { updateManifest } from './utils/manifest';
import {
  DashboardPage,
  RevenueDashboard,
  BranchesPage,
  MappingsPage,
  OrdersPage,
  ReportsPage,
  LoginPage,
  ProfilePage,
  UserManagementPage,
  RoleManagementPage,
  SettingsPage,
  NotFoundPage,
} from './pages';

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);

  useEffect(() => {
    dispatch(initializeAuth());
    
    // Update manifest with correct app name
    updateManifest();
    
    // Update document title
    document.title = APP_CONFIG.APP_TITLE;
    
    // Register service worker for PWA functionality
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
            
            // Check for updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // New content is available, prompt user to reload
                    if (window.confirm('New version available! Click OK to update.')) {
                      newWorker.postMessage({ type: 'SKIP_WAITING' });
                      window.location.reload();
                    }
                  }
                });
              }
            });
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }, [dispatch]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - requires order read permission */}
        <Route 
          index 
          element={
            <ProtectedRoute requirePermission="orders:read" showAccessDenied={false} redirectTo="/redirect">
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Smart redirect for users without dashboard access */}
        <Route path="/redirect" element={<SmartRedirect />} />
        
        {/* Revenue Dashboard - requires revenue read permission */}
        <Route 
          path="revenue" 
          element={
            <ProtectedRoute requirePermission="revenue:read">
              <RevenueDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Branches - requires branch read permission */}
        <Route 
          path="branches" 
          element={
            <ProtectedRoute requirePermission="branches:read">
              <BranchesPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Mappings - requires mapping read permission */}
        <Route 
          path="mappings" 
          element={
            <ProtectedRoute requirePermission="mappings:read">
              <MappingsPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Orders - requires order read permission */}
        <Route 
          path="orders" 
          element={
            <ProtectedRoute requirePermission="orders:read">
              <OrdersPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Reports - requires reports read permission */}
        <Route path="reports">
          {/* Main reports page */}
          <Route 
            index
            element={
              <ProtectedRoute requirePermission="reports:read">
                <ReportsPage />
              </ProtectedRoute>
            } 
          />
          
          {/* All additional report routes removed, keeping only the main reports page */}
        </Route>
        
        {/* User management routes with specific permissions */}
        <Route
          path="users"
          element={
            <ProtectedRoute requirePermission="users:read">
              <UserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="roles"
          element={
            <ProtectedRoute requirePermission="roles:read">
              <RoleManagementPage />
            </ProtectedRoute>
          }
        />
        
        {/* Settings - basic access for all users, advanced settings protected internally */}
        <Route 
          path="settings" 
          element={<SettingsPage />} 
        />
        
        {/* Profile - available to all authenticated users */}
        <Route 
          path="profile" 
          element={<ProfilePage />} 
        />
        
        {/* Placeholder routes */}
        <Route path="notifications" element={<div>Notifications Page (Coming Soon)</div>} />
      </Route>

      {/* Catch all route */}
      <Route
        path="*"
        element={
          isAuthenticated ? <NotFoundPage /> : <Navigate to="/login" replace />
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router>
        <AppContent />
        <PWAInstallPrompt />
      </Router>
    </Provider>
  );
};

export default App;
